// AttachmentFieldScan.jsx — container for the Existing Attachment Field
// Scan tool (design_handoff_attachment_field_scan). Owns all data:
//
//   · field catalog synced from the customer's FMX Equipment fields
//   · equipment list + dependency caches (building / type names)
//   · the per-equipment OCR cache (equipment_ocr_results) → review queue
//   · the applied-write log (equipment_scan_writes) → history + revert
//   · the batch scan engine (one runOcrOnEquipment call per record)
//
// Renders ScanDesk or ScanHistory; the view choice persists per project in
// localStorage. Nothing writes to FMX without an explicit apply.

import { useEffect, useMemo, useRef, useState } from 'react';
import { T } from '../../theme';
import { syncFmxDataForProject } from '../../fmxSync';
import {
  listEquipment, getEquipment, runOcrOnEquipment, fetchAttachmentForOcr,
  updateEquipment, fieldAppliesToEquipment, listLifespanTrackedTypeIDs,
  ASSET_LIFESPAN_SYSTEM_KEYS,
} from '../../equipmentOcr';
import { buildEquipmentPutPayload, isScannableEquipmentField } from '../../equipmentScanWrite';
import { ASSET_CONDITION_LABELS, ASSET_CATEGORY_LABELS } from '../../fmxFieldMetadata';
import {
  getAllDependencyCaches, listCachedEquipmentIds, getEquipmentOcrResultsByEquipmentIds,
  listScanWrites, createScanWrite, setScanWriteReverted, getOcrSpendUsd, updateProject,
} from '../../db';
import { normalizeConf } from './scanShared';
import ScanDesk from './ScanDesk';
import ScanHistory from './ScanHistory';

// System fields that make sense as scan targets by default; everything else
// starts toggled off (the slideout can switch any field on).
const DEFAULT_ON_SYSTEM = /model|serial|manufactur|asset.?tag/i;

const viewKey = (projectId) => `afsView:${projectId}`;

// Collapse an equipment's cache rows (one per attachment) into a single
// label → entry map, preferring higher confidence — same merge rule the
// live scan uses.
function mergeCacheRows(rows) {
  const fields = {};
  const fileNames = {};
  let lastUpdated = null;
  for (const r of rows || []) {
    if (r.attachment_id != null) fileNames[String(r.attachment_id)] = r.attachment_filename || null;
    const t = r.updated_at ? new Date(r.updated_at).getTime() : 0;
    if (!lastUpdated || t > lastUpdated) lastUpdated = t;
    for (const [label, entry] of Object.entries(r.fields || {})) {
      if (!entry || entry.value == null || entry.value === '') continue;
      const incoming = { ...entry, source_attachment_id: entry.source_attachment_id || r.attachment_id };
      const existing = fields[label];
      if (!existing || Number(incoming.confidence ?? 0) > Number(existing.confidence ?? 0)) fields[label] = incoming;
    }
  }
  return { fields, fileNames, lastUpdated };
}

function currentValueOf(equipment, def) {
  if (!equipment) return null;
  let v = null;
  if (def.kind === 'system') {
    v = equipment[def.key];
  } else {
    for (const cf of equipment.customFields || []) {
      const k = cf.customFieldID ?? cf.customFieldId ?? cf.id;
      if (String(k) === String(def.key)) {
        // Multi-select fields carry `values`; join with '; ' so the captured
        // old value round-trips through splitMultiValue on revert.
        v = cf.value ?? (Array.isArray(cf.values) && cf.values.length ? cf.values.join('; ') : null);
        break;
      }
    }
  }
  if (v == null || v === '') return null;
  return typeof v === 'object' ? JSON.stringify(v) : String(v);
}

export default function AttachmentFieldScan({ project, currentProfile }) {
  const projectId = project?.id;
  const [view, setView] = useState('desk');
  // Two-phase load: Supabase data (queue cache, writes, spend) renders the
  // screen immediately; the FMX field catalog + equipment list hydrate in
  // the background. Nothing here calls Claude — scans are user-initiated.
  const [baseLoading, setBaseLoading] = useState(true); // Supabase phase
  const [fmxLoading, setFmxLoading] = useState(true);   // FMX phase
  const [fmxError, setFmxError] = useState(null);
  const [catalog, setCatalog] = useState(null);      // { systemFields, customFields }
  const [syncedAt, setSyncedAt] = useState(null);
  const [equipment, setEquipment] = useState([]);
  const [deps, setDeps] = useState({ buildings: [], equipmentTypes: [] });
  const [lifespanTypeIDs, setLifespanTypeIDs] = useState(null); // Set<string> of type IDs that track asset lifespan; null until resolved
  const [cacheByEq, setCacheByEq] = useState(new Map()); // eqId → { fields, fileNames, lastUpdated }
  const [writes, setWrites] = useState([]);
  const [spendUsd, setSpendUsd] = useState(0);
  const [fieldsOff, setFieldsOff] = useState(null);  // Set of field ids; null until catalog ready
  const [scan, setScan] = useState(null);            // { running, done, total, finished, records, status }
  const attCacheRef = useRef(new Map());             // eqId → [{ id, filename, classification, contentType, base64 }]
  const scanDoneTimerRef = useRef(null);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    setBaseLoading(true);
    setFmxLoading(true);
    setFmxError(null);
    setCacheByEq(new Map());
    setCatalog(null);
    setFieldsOff(null);
    setEquipment([]);
    setLifespanTypeIDs(null);
    attCacheRef.current = new Map();
    setView(localStorage.getItem(viewKey(projectId)) || 'desk');

    // Phase A — Supabase only (fast): cached scan results, the write log,
    // dependency name maps, and spend. The screen renders from this.
    (async () => {
      try {
        const [caches, cachedIds, writeRows, spend] = await Promise.all([
          getAllDependencyCaches(projectId).catch(() => []),
          listCachedEquipmentIds(projectId),
          listScanWrites(projectId),
          getOcrSpendUsd(projectId),
        ]);
        if (cancelled) return;
        const byKey = Object.fromEntries((caches || []).map(r => [r.schema_type, r.extra?.items || []]));
        setDeps({ buildings: byKey['buildings'] || [], equipmentTypes: byKey['equipment-types'] || [] });
        setWrites(writeRows || []);
        setSpendUsd(spend || 0);

        const rowsByEq = await getEquipmentOcrResultsByEquipmentIds(projectId, Array.from(cachedIds));
        if (cancelled) return;
        const merged = new Map();
        for (const [eqId, rows] of rowsByEq) merged.set(eqId, mergeCacheRows(rows));
        setCacheByEq(merged);
      } finally {
        if (!cancelled) setBaseLoading(false);
      }
    })();

    // Phase B — FMX, in the background: field catalog (cache-first, NO
    // Claude type inference, no shared-cache write) then the equipment
    // list. Failures surface as a banner, not a blocked screen.
    (async () => {
      try {
        const cat = await syncFmxDataForProject(project, 'Equipment', false, { inferTypes: false, writeCache: false });
        if (cancelled) return;
        // A real FMX site always returns system fields (tag, buildingID, …).
        // An empty catalog means the sync failed upstream (fmxSync swallows
        // fetch errors and returns empties) — surface it instead of silently
        // rendering a field list with everything missing.
        if (!(cat.systemFields || []).length && !(cat.customFields || []).length) {
          throw new Error('FMX returned no Equipment fields — the FMX connection may be failing or the saved credentials were rejected. Re-verify credentials in Settings → FMX, then reload.');
        }
        setCatalog({ systemFields: cat.systemFields || [], customFields: cat.customFields || [] });
        setSyncedAt(Date.now());

        // Resolve which equipment types track asset lifespan (Capital Planner).
        // Drives per-record gating and the default-ON decision below. On
        // failure, leave null → the lifespan gate is skipped and the fields
        // default off (conservative).
        let lifespanIDs = null;
        try {
          lifespanIDs = await listLifespanTrackedTypeIDs(projectId);
          if (cancelled) return;
          setLifespanTypeIDs(lifespanIDs);
        } catch {
          if (!cancelled) setLifespanTypeIDs(null);
        }
        const lifespanAvailable = !!(lifespanIDs && lifespanIDs.size > 0);

        // Field toggles: [] (column default) = never configured → seed the
        // defaults; a saved config is stored as { off: [...] }.
        const saved = project.scan_fields_off;
        if (saved && !Array.isArray(saved) && Array.isArray(saved.off)) {
          setFieldsOff(new Set(saved.off));
        } else {
          const off = new Set();
          for (const sf of cat.systemFields || []) {
            const label = sf.label || sf.key;
            if (ASSET_LIFESPAN_SYSTEM_KEYS.has(sf.key)) {
              // Asset lifespan fields default ON only when the capability is
              // available on this site (at least one type tracks it).
              if (!lifespanAvailable) off.add(`sys:${sf.key}`);
            } else if (!DEFAULT_ON_SYSTEM.test(label)) {
              off.add(`sys:${sf.key}`);
            }
          }
          setFieldsOff(off);
        }

        // Equipment list wants the system field keys so the "Current:" line
        // can show system values without a per-record GET.
        const sysKeys = (cat.systemFields || []).filter(f => f.isPermitted !== false).map(f => f.key);
        let eq;
        try {
          eq = await listEquipment(projectId, sysKeys);
        } catch {
          eq = await listEquipment(projectId); // some sites reject extra field keys
        }
        if (cancelled) return;
        setEquipment(eq || []);
      } catch (e) {
        if (!cancelled) setFmxError(e?.message || 'Failed to load equipment from FMX');
      } finally {
        if (!cancelled) setFmxLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [projectId]);

  useEffect(() => () => { if (scanDoneTimerRef.current) clearTimeout(scanDoneTimerRef.current); }, []);

  const switchView = (v) => {
    setView(v);
    if (projectId) localStorage.setItem(viewKey(projectId), v);
  };

  // ---- derived ----

  const fieldDefs = useMemo(() => {
    if (!catalog || !fieldsOff) return [];
    const found = new Map();
    for (const { fields } of cacheByEq.values()) {
      for (const label of Object.keys(fields)) found.set(label, (found.get(label) || 0) + 1);
    }
    const defs = [];
    for (const sf of catalog.systemFields) {
      if (sf.isPermitted === false) continue;
      // Integer-lookup system fields (buildingID etc.) can't take OCR text.
      if (!isScannableEquipmentField({ kind: 'system', key: sf.key })) continue;
      // The asset-lifespan enum fields carry no options in post-options, so
      // attach the allowed labels here — buildUserPromptText surfaces them to
      // Claude via describeFieldOptions.
      let raw = sf;
      if (sf.key === 'assetCondition') raw = { ...sf, options: ASSET_CONDITION_LABELS };
      else if (sf.key === 'assetCategory') raw = { ...sf, options: ASSET_CATEGORY_LABELS };
      defs.push({ id: `sys:${sf.key}`, key: sf.key, label: sf.label || sf.key, kind: 'system', fieldType: 'system', group: 'Standard', raw });
    }
    const customs = catalog.customFields
      // ReadOnly / Attachments / User custom fields aren't writable from OCR text.
      .filter(cf => isScannableEquipmentField({ kind: 'custom', key: cf.id, fieldType: cf.fieldType || 'Text' }))
      .map(cf => ({ id: `cf:${cf.id}`, key: cf.id, label: cf.name, kind: 'custom', fieldType: cf.fieldType || 'Text', group: 'Custom fields', raw: cf }))
      .sort((a, b) => a.label.localeCompare(b.label));
    defs.push(...customs);
    return defs.map(d => ({ ...d, on: !fieldsOff.has(d.id), found: found.get(d.label) || 0 }));
  }, [catalog, fieldsOff, cacheByEq]);

  const buildingName = useMemo(() => new Map(deps.buildings.map(b => [String(b.id), b.name])), [deps.buildings]);
  const typeName = useMemo(() => new Map(deps.equipmentTypes.map(t => [String(t.id), t.name])), [deps.equipmentTypes]);

  const queue = useMemo(() => {
    const items = [];
    for (const eq of equipment) {
      const merged = cacheByEq.get(String(eq.id));
      if (!merged) continue;
      // Per-record scoping: another type's custom fields genuinely don't
      // belong to this record (FMX scopes each record's customFields array
      // to its type) — hide them rather than showing a permanently blank row.
      const applicable = fieldDefs.filter(d => fieldAppliesToEquipment(d, eq, lifespanTypeIDs));
      const fields = applicable.map(d => {
        const entry = merged.fields[d.label];
        const srcId = entry?.source_attachment_id != null ? String(entry.source_attachment_id) : null;
        return {
          label: d.label, kind: d.kind, key: d.key, fieldType: d.fieldType,
          value: entry?.value != null && entry.value !== '' ? String(entry.value) : null,
          conf: normalizeConf(entry?.confidence),
          srcId,
          srcName: srcId ? (merged.fileNames[srcId] || `#${srcId}`) : null,
          cur: currentValueOf(eq, d),
        };
      });
      items.push({
        id: String(eq.id),
        tag: eq.tag || `#${eq.id}`,
        type: typeName.get(String(eq.equipmentTypeID ?? '')) || '',
        bld: buildingName.get(String(eq.buildingID ?? '')) || '—',
        scannedAt: merged.lastUpdated,
        attachmentIDs: eq.attachmentIDs || [],
        fields,
      });
    }
    return items.sort((a, b) => (b.scannedAt || 0) - (a.scannedAt || 0));
  }, [equipment, cacheByEq, fieldDefs, buildingName, typeName, lifespanTypeIDs]);

  const appliedIds = useMemo(() => {
    const s = new Set();
    for (const w of writes) if (!w.reverted) s.add(String(w.equipment_id));
    return s;
  }, [writes]);

  const unscannedList = useMemo(
    () => equipment.filter(e => Array.isArray(e.attachmentIDs) && e.attachmentIDs.length > 0 && !cacheByEq.has(String(e.id))),
    [equipment, cacheByEq]
  );

  // Display rows for the kickoff modal's scope picker.
  const unscannedItems = useMemo(() => unscannedList.map(e => ({
    id: String(e.id),
    tag: e.tag || `#${e.id}`,
    type: typeName.get(String(e.equipmentTypeID ?? '')) || '',
    bld: buildingName.get(String(e.buildingID ?? '')) || '—',
    attachmentCount: e.attachmentIDs.length,
  })), [unscannedList, typeName, buildingName]);

  const stats = {
    scanned: cacheByEq.size,
    appliedCount: appliedIds.size,
    spendUsd,
  };

  // ---- actions ----

  const loadAttachments = async (item) => {
    const key = String(item.id);
    if (attCacheRef.current.has(key)) return attCacheRef.current.get(key);
    let failures = 0;
    const results = await Promise.all((item.attachmentIDs || []).map(async (attId) => {
      try {
        const a = await fetchAttachmentForOcr(projectId, attId);
        if (!a.base64) return null; // unsupported type — deterministic skip, not a failure
        return {
          id: a.meta?.id ?? attId, filename: a.meta?.filename || a.filename || null,
          classification: a.classification, contentType: a.contentType, base64: a.base64,
        };
      } catch {
        failures += 1;
        return null;
      }
    }));
    const list = results.filter(Boolean);
    // Only cache clean loads. A transient /api/fmx failure (timeout, stale
    // creds, FMX hiccup) must not pin an empty/partial list for the rest of
    // the session — leaving it uncached retries on the next open.
    if (failures === 0) attCacheRef.current.set(key, list);
    return list;
  };

  const handleApply = async (item, rows) => {
    // Fresh GET for accurate `old` values (the PUT itself sends only the
    // accepted rows — FMX merges customFields per-field, so untouched
    // fields survive server-side without being echoed).
    const full = await getEquipment(projectId, item.id);
    const defByLabel = new Map(fieldDefs.map(d => [d.label, d]));
    const detail = rows.map(r => {
      const def = defByLabel.get(r.label) || null;
      const cur = currentValueOf(full, def || r);
      return { ...r, def, old: cur != null && cur !== '' ? String(cur) : null };
    });
    const { payload, skipped } = buildEquipmentPutPayload(
      detail.map(r => ({ kind: r.kind, key: r.key, label: r.label, value: r.value, def: r.def }))
    );
    if (skipped.length === detail.length) {
      throw new Error(`Nothing writable: ${skipped.map(s => `${s.label} (${s.reason})`).join('; ')}`);
    }
    await updateEquipment(projectId, item.id, payload);
    // Log only what was actually written so history/revert never claims a
    // value FMX didn't receive.
    const skippedKeys = new Set(skipped.map(s => String(s.key)));
    const written = detail.filter(r => !skippedKeys.has(String(r.key)));

    const maxBatch = writes.reduce((m, w) => Math.max(m, w.batch_num || 0), 0);
    const myBatch = writes.some(w => w.sessionBatch) ? writes.find(w => w.sessionBatch).batch_num : maxBatch + 1;
    const row = await createScanWrite({
      projectId,
      equipmentId: item.id,
      equipmentTag: item.tag,
      buildingName: item.bld,
      equipmentTypeName: item.type,
      batchNum: myBatch,
      appliedBy: currentProfile?.id || null,
      appliedByName: currentProfile?.full_name || currentProfile?.email || null,
      writes: written.map(r => ({
        label: r.label, kind: r.kind, key: r.key, old: r.old, value: r.value,
        confidence: r.confidence, edited: !!r.edited,
        sourceAttachmentId: r.sourceAttachmentId, sourceFilename: r.sourceFilename,
      })),
    });
    // db.js swallows insert errors (returns null) — keep the UI consistent
    // with the FMX write that already succeeded.
    const logged = row || {
      id: `local-${Date.now()}`, equipment_id: item.id, equipment_tag: item.tag,
      building_name: item.bld, equipment_type_name: item.type, batch_num: myBatch,
      applied_by_name: currentProfile?.full_name || currentProfile?.email || null,
      writes: written, reverted: false, created_at: new Date().toISOString(),
    };
    setWrites(ws => [{ ...logged, sessionBatch: true }, ...ws]);
  };

  const handleRevert = async (r) => {
    // null (not '') clears properly — empty string fails on typed system
    // fields. Legacy string `old` values re-coerce via the field def; a
    // missing def (catalog not loaded / field since deleted) falls back to
    // raw-string passthrough inside the builder.
    const defByLabel = new Map(fieldDefs.map(d => [d.label, d]));
    const rows = (r.writes || []).map(w => ({
      kind: w.kind, key: w.key, label: w.label,
      value: w.old ?? null, def: defByLabel.get(w.label) || null,
    }));
    const { payload } = buildEquipmentPutPayload(rows);
    await updateEquipment(projectId, r.equipment_id, payload);
    await setScanWriteReverted(r.id);
    setWrites(ws => ws.map(x => (x.id === r.id ? { ...x, reverted: true, reverted_at: new Date().toISOString() } : x)));
  };

  const handleToggleField = (id) => {
    setFieldsOff(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      updateProject(projectId, { scan_fields_off: { off: Array.from(next) } });
      return next;
    });
  };

  // `selectedIds` comes from the kickoff modal's scope picker — only those
  // records are scanned. The scan never starts on its own. Two records scan
  // concurrently; each finished one lands in the review queue immediately.
  const handleStartScan = async (selectedIds) => {
    if (scan?.running) return;
    const wanted = selectedIds instanceof Set ? selectedIds : new Set(selectedIds || []);
    const targets = unscannedList.filter(e => wanted.has(String(e.id)));
    if (!targets.length) return;
    if (scanDoneTimerRef.current) clearTimeout(scanDoneTimerRef.current);
    const itemById = new Map(unscannedItems.map(r => [r.id, r]));
    const records = targets.map(t =>
      itemById.get(String(t.id)) || { id: String(t.id), tag: t.tag || `#${t.id}`, type: '', bld: '—' });
    setScan({
      running: true, done: 0, total: targets.length, finished: false,
      records,
      status: Object.fromEntries(records.map(r => [r.id, 'queued'])),
      errors: {},
    });
    const setStatus = (id, st, errMsg) => setScan(s => (s ? {
      ...s,
      status: { ...s.status, [id]: st },
      done: st === 'done' || st === 'error' ? s.done + 1 : s.done,
      errors: errMsg ? { ...s.errors, [id]: errMsg } : s.errors,
    } : s));
    const fieldSelection = fieldDefs.filter(d => d.on)
      .map(d => ({ id: d.id, key: d.key, label: d.label, kind: d.kind, fieldType: d.fieldType, raw: d.raw }));
    let next = 0;
    const worker = async () => {
      for (;;) {
        const i = next++;
        if (i >= targets.length) return;
        const t = targets[i];
        const id = String(t.id);
        setStatus(id, 'scanning');
        try {
          const full = await getEquipment(projectId, t.id);
          // Narrow the project-level selection to this record's own type
          // fields. runOcrOnEquipment throws on an empty selection, so skip
          // with a readable status instead.
          const perItemSelection = fieldSelection.filter(f => fieldAppliesToEquipment(f, full, lifespanTypeIDs));
          if (!perItemSelection.length) {
            setStatus(id, 'error', 'No enabled fields apply to this record — check Fields to scan');
            continue;
          }
          const res = await runOcrOnEquipment({
            projectId, userId: currentProfile?.id, equipment: full, fieldSelection: perItemSelection,
          });
          // Keep the bytes Claude saw so review opens instantly, and feed the
          // result into the queue as it lands.
          const previewable = (res.attachments || []).filter(a => !a.skipped && a.base64);
          attCacheRef.current.set(id, previewable);
          const fileNames = {};
          for (const a of res.attachments || []) if (a.id != null) fileNames[String(a.id)] = a.filename || null;
          const fields = res.parsed?.fields || {};
          // Every finished record lands in the review queue immediately —
          // even with zero extracted values (per-attachment failures are
          // swallowed inside runOcrOnEquipment and surface as an empty
          // result). Blank inputs are still reviewable: the specialist can
          // hand-fill them from the attachment viewer. Gating this on
          // non-empty fields silently bounced records back to "unscanned".
          setCacheByEq(prev => {
            const nextMap = new Map(prev);
            nextMap.set(id, { fields, fileNames, lastUpdated: Date.now() });
            return nextMap;
          });
          setStatus(id, 'done');
        } catch (e) {
          // Per-record failures shouldn't sink the batch; the record simply
          // stays unscanned and can be retried on the next kickoff.
          console.error(`Attachment Field Scan: equipment ${id} failed`, e);
          setStatus(id, 'error', e?.message || 'Scan failed');
        }
      }
    };
    await Promise.all([worker(), worker()]);
    setScan(s => (s ? { ...s, running: false, finished: true } : s));
    scanDoneTimerRef.current = setTimeout(() => setScan(null), 5000);
    getOcrSpendUsd(projectId).then(setSpendUsd);
  };

  // ---- gates / shell states ----

  if (!project) return <ScanNotice>Select a project to use this tool.</ScanNotice>;
  if (!project.fmx_connection_verified || !project.fmx_site_url) {
    return (
      <ScanNotice>
        This project has no verified FMX credentials. Save credentials in Settings → FMX before running a scan.
      </ScanNotice>
    );
  }
  if (view === 'history') {
    return (
      <ScanHistory
        key={projectId}
        project={project}
        projectName={project.name}
        writes={writes}
        onBack={() => switchView('desk')}
        onRevert={handleRevert}
      />
    );
  }

  return (
    <ScanDesk
      key={projectId}
      project={project}
      projectName={project.name}
      queue={queue}
      fieldDefs={fieldDefs}
      stats={stats}
      scan={scan}
      appliedIds={appliedIds}
      unscannedItems={unscannedItems}
      hydrating={baseLoading || fmxLoading}
      fmxError={fmxError}
      syncedAt={syncedAt}
      loadAttachments={loadAttachments}
      onApply={handleApply}
      onStartScan={handleStartScan}
      onToggleField={handleToggleField}
      onOpenHistory={() => switchView('history')}
    />
  );
}

function ScanNotice({ tone, children }) {
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', background: T.bg, padding: '48px 24px' }}>
      <div style={{
        background: T.white, border: `1px solid ${tone === 'error' ? T.badge.red : T.borderSubtle}`,
        borderRadius: T.radiusCard, padding: '24px 28px', maxWidth: 520,
        font: `400 14px/20px ${T.fontSans}`, color: tone === 'error' ? T.red500 : T.inkMuted,
      }}>
        {children}
      </div>
    </div>
  );
}
